import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import SuccessSvg from '@clearscore/rainbow.icons.status-success';
import InformationSvg from '@clearscore/rainbow.icons.status-info';
import AttentionSvg from '@clearscore/rainbow.icons.status-warning';
import AlertErrorSvg from '@clearscore/rainbow.icons.status-error';
import CloseSimple from '@clearscore/rainbow.icons.cross';
import ClickableIcon from '@clearscore/rainbow.clickable-icon';
import Text from '@clearscore/rainbow.text';
import Button from '@clearscore/rainbow.button';
import toTitleCase from '@clearscore/normalize.title-case';

import { themes } from './constants';
import styles from './notification.css';

const getIcon = ({ theme, ...props }) =>
    ({
        [themes.SUCCESS]: <SuccessSvg {...props} />,
        [themes.INFO]: <InformationSvg {...props} />,
        [themes.WARNING]: <AttentionSvg {...props} />,
        [themes.ERROR]: <AlertErrorSvg {...props} />,
    }[theme] || <InformationSvg {...props} />);

getIcon.propTypes = { theme: PropTypes.oneOf([...Object.values(themes), 'DEFAULT']) };
getIcon.defaultProps = { theme: null };

const cssTheme = (theme) => themes[theme] || themes.INFO;

const Notification = ({
    dataId,
    children,
    theme,
    ctaText,
    ctaHandler,
    title,
    onDismiss,
    href,
    isDismissible,
    isNewTab,
}) => (
    <div
        className={cx(styles.component, styles[`isTheme${toTitleCase(cssTheme(theme))}`], {
            [styles.dismissable]: isDismissible,
        })}
        role="alert"
        data-qa="notification"
        data-id={dataId}
    >
        <div className={styles.icon}>{getIcon({ theme })}</div>

        <div className={styles.text}>
            <If condition={title}>
                <Text.H4>{title}</Text.H4>
            </If>
            {children}
        </div>
        <Choose>
            <When condition={ctaText && href}>
                <div className={styles.ctaLink}>
                    <Text
                        dataId="cta"
                        href={href}
                        weight={Text.weights.BOLD}
                        isNewTab={isNewTab}
                        type={Text.types.MINI}
                        onClick={ctaHandler}
                    >
                        {ctaText}
                    </Text>
                </div>
            </When>
            <When condition={ctaText}>
                <Button isFlat data-qa="notification-cta" type={Button.types.PRIMARY} isNarrow onClick={ctaHandler}>
                    {ctaText}
                </Button>
            </When>
        </Choose>
        <If condition={isDismissible}>
            <div className={styles.closeIcon} data-qa="notification-dismiss">
                <ClickableIcon onClick={onDismiss}>
                    <CloseSimple height={24} width={24} />
                </ClickableIcon>
            </div>
        </If>
    </div>
);

Notification.propTypes = {
    children: PropTypes.node.isRequired,
    theme: PropTypes.oneOf([...Object.values(themes), 'DEFAULT']),
    title: PropTypes.string,
    ctaText: PropTypes.string,
    href: PropTypes.string,
    ctaHandler: PropTypes.func,
    onDismiss: PropTypes.func,
    isDismissible: PropTypes.bool,
    isNewTab: PropTypes.bool,
    dataId: PropTypes.string,
};

Notification.defaultProps = {
    theme: themes.INFO,
    ctaText: '',
    href: '',
    ctaHandler: null,
    title: '',
    onDismiss: () => {},
    isDismissible: true,
    isNewTab: false,
    dataId: '',
};

Notification.themes = themes;

export default Notification;
